/* ─────────────────────────────────────────────────────────────
   The previous Schedule Logs page — antd Row/Col around the antd-backed
   CustomTable, fetching resources (connections) it never used, with the inner
   delivery table built by a handleInnerTable() helper and the same rows reused
   for every parent. Rebuilt below on the section layout and the shared table.
   Previous version in git history.
   ───────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SchedulePageLayout from "./SchedulePageLayout";
import CustomTable from "../../components/common/DataTable";
import ShareReportModal from "./ShareReportModal";
import { fetchScheduleLogs, actionError } from "./scheduleApi";
import { rowFromScheduleLog } from "./scheduleMappers";
import "./ScheduleLogs.css";

/**
 * The delivery history of one schedule — reached from the logs icon on the
 * Scheduled Reports list.
 *
 * Each row is one run. Expanding it shows who that run went to and what
 * happened to each of them; the share icon re-sends the run to someone else.
 *
 * Fed by POST /v1/scheduler/task/logs, which narrows to one schedule when given a
 * ScheduleID — the id this page takes from its route. It ran on a mock file until that
 * endpoint existed, which meant every schedule showed the same invented history.
 *
 * The response shows the last 60 runs and no more — see LOG_PAGE_SIZE — so there is no
 * pager here on purpose.
 *
 * One control still has nothing behind it: Re-Send, which has no endpoint. See
 * handleResend.
 */

const Icon = {
  back: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path d="M9.5 3.5L5 8l4.5 4.5M5 8h7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  share: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <circle cx="12" cy="3.6" r="1.9" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="4" cy="8" r="1.9" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="12" cy="12.4" r="1.9" stroke="currentColor" strokeWidth="1.3" />
      <path d="M10.3 4.6L5.7 7M5.7 9l4.6 2.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.8" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 4.6l6 4 6-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  resend: (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true">
      <path
        d="M13.4 7a5.5 5.5 0 10-1.6 4.2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M13.6 3.2v3.9h-3.9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const StatusText = ({ status }) => (
  <span className={`slg-status slg-status-${String(status || "").toLowerCase().replace(/\s+/g, "-")}`}>{status}</span>
);

export default function ScheduleLogsTable() {
  const history = useHistory();
  const { scheduleId } = useParams();

  const [shareLog, setShareLog] = useState(null);
  const [logs, setLogs] = useState([]);
  const [listState, setListState] = useState("loading"); // loading | ready | error

  /* The organisation comes from localStorage, where login writes it - the same source the
     schedules list uses. The schedule id comes from the route, and narrows the response
     to this one schedule's runs; without it the endpoint returns the whole organisation's
     history, which is what the /schedule-logs path (no id) shows. */
  const load = useCallback(async () => {
    setListState("loading");
    try {
      const records = await fetchScheduleLogs({
        organizationID: localStorage.getItem("orgID") || "",
        scheduleID: scheduleId || "",
      });
      setLogs(records.map(rowFromScheduleLog));
      setListState("ready");
    } catch (error) {
      setLogs([]);
      setListState("error");
      toast.error(actionError(error, "Loading schedule logs failed."));
    }
  }, [scheduleId]);

  useEffect(() => {
    load();
  }, [load]);

  /* Re-Send is shown disabled rather than hidden.
     There is no endpoint for it — the scheduler team confirmed on 2026-09-12 that it is
     not implemented yet. It used to run a 1.2s timer and then say nothing, which looked
     exactly like a successful re-send and was worse than no button at all.
     The wiring goes back here once the endpoint exists. */
  const RESEND_UNAVAILABLE = "Re-sending is not available yet — the scheduler service has no endpoint for it.";

  /* The accordion under an expanded run. Built here rather than as a nested
     CustomTable: it has no pager, no frame and no scroll region of its own. */
  const deliveriesTable = (log) => (
    <div className="slg-deliveries">
      <table className="slg-inner-table">
        <thead>
          {/* No User column. UserLogs carries an Email and no name, and the scheduler team
              confirmed it never will — so the column could only ever have shown a dash. */}
          <tr>
            <th className="slg-inner-th">User email</th>
            <th className="slg-inner-th">Schedule Type</th>
            <th className="slg-inner-th">Delivered Date Time</th>
            <th className="slg-inner-th">Status</th>
            <th className="slg-inner-th">Action</th>
          </tr>
        </thead>
        <tbody>
          {/* Most runs come back with an empty UserLogs, so the accordion needs something
              to say rather than opening onto a bare header row. */}
          {log.deliveries.length === 0 ? (
            <tr className="slg-inner-tr">
              <td className="slg-inner-td slg-inner-empty" colSpan={5}>
                No delivery details recorded for this run.
              </td>
            </tr>
          ) : null}
          {log.deliveries.map((d) => (
            <tr className="slg-inner-tr" key={d.id}>
              <td className="slg-inner-td">
                <span className="slg-email">
                  {Icon.mail}
                  {d.email}
                </span>
              </td>
              <td className="slg-inner-td">{d.scheduleType}</td>
              <td className="slg-inner-td">{d.deliveredAt}</td>
              <td className="slg-inner-td">
                <StatusText status={d.status} />
              </td>
              <td className="slg-inner-td">
                <button type="button" className="slg-resend" disabled title={RESEND_UNAVAILABLE}>
                  {Icon.resend}
                  Re-Send
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  /* `expandedData` is what CustomTable renders when a row is opened. */
  const rows = useMemo(
    () => logs.map((log) => ({ ...log, expandedData: deliveriesTable(log) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logs],
  );

  const columns = [
    {
      title: "Report Name",
      dataIndex: "reportName",
      key: "reportName",
      nowrap: true,
      render: (value) => <span className="slg-report-name">{value}</span>,
    },
    { title: "Frequency", dataIndex: "frequency", key: "frequency", muted: true, nowrap: true },
    { title: "File Type", dataIndex: "fileType", key: "fileType", muted: true, nowrap: true },

    /* Was "Email Scheduled Date", reading a field that held the schedule's next due time —
       so every row of a schedule showed one identical date, in the future, while the
       delivery beside it had happened the day before. TaskDate is this run. */
    { title: "Execution Date", dataIndex: "taskDate", key: "taskDate", muted: true, nowrap: true },

    /* The same on every row of a schedule, because it describes the schedule rather than
       the run. Kept because it is the one place outside the summary panel that says when
       the next one is due. */
    { title: "Next Execution", dataIndex: "nextScheduledDate", key: "nextScheduledDate", muted: true, nowrap: true },

    { title: "Time Zone", dataIndex: "timeZone", key: "timeZone", muted: true, nowrap: true },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      nowrap: true,
      render: (value) => <StatusText status={value} />,
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      nowrap: true,
      render: (_value, row) => (
        <button
          type="button"
          className="slg-action-btn"
          title="Share this report"
          onClick={(e) => {
            // The row toggles the accordion, so the icon must not do both.
            e.stopPropagation();
            setShareLog(row);
          }}
        >
          {Icon.share}
        </button>
      ),
    },
  ];

  return (
    <SchedulePageLayout
      title="Schedule Logs"
      subtitle={scheduleId ? `Logs for schedule #${scheduleId}` : "Logs for all schedules"}
      actionLabel="Back"
      actionIcon={Icon.back}
      onAction={() => history.push("/scheduled-reports")}
    >
      {/* Failures are toasted, but a toast disappears — so the retry lives in the empty
          state, which is where anyone looks when the table has nothing in it. Same
          pattern as the schedules list. */}
      <CustomTable
        tableHeaders={columns}
        tableData={rows}
        rowKey="id"
        hasExpandedRows
        loading={listState === "loading"}
        emptyText={
          listState === "error" ? (
            <div className="slg-empty-error">
              <span>Could not load the schedule logs.</span>
              <button type="button" className="slg-retry-btn" onClick={load}>
                Retry
              </button>
            </div>
          ) : (
            "No delivery logs for this schedule yet."
          )
        }
        /* Grown from 980 for the Next Run column - below this the row would squeeze
           rather than the table scrolling, and two dates side by side need the space. */
        minWidth={1120}
      />

      <ShareReportModal
        open={Boolean(shareLog)}
        log={shareLog}
        onClose={() => setShareLog(null)}
        onShare={(payload) => {
          // Nothing is sent yet — this is where the share endpoint goes.
          // eslint-disable-next-line no-console
          console.log("share report", { logId: shareLog?.id, ...payload });
          setShareLog(null);
        }}
      />
    </SchedulePageLayout>
  );
}
